import { createFileRoute, Link, Outlet, useNavigate, useMatchRoute } from '@tanstack/react-router';
import { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { FaUserCircle } from "react-icons/fa";
import ArticlesIconSvg from '@/assets/icons/article.svg?react';
import NotificationIconSvg from '@/assets/icons/bell.svg?react';
import QuizzesIconSvg from '@/assets/icons/quiz.svg?react';
import UserManagementIconSvg from '@/assets/icons/users.svg?react';
import CampusResourcesIconSvg from '@/assets/icons/campusResources.svg?react';
import logoHorizontal from '@/assets/images/Logo-horizontal.png';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { IoAnalyticsOutline } from "react-icons/io5";

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
});

function AdminLayout() {
  const { logout, isAuthenticated, isAdmin, isLoading, userData } = useAuth();
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [sidebarSize, setSidebarSize] = useState(20);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Show only icons when sidebar is smaller than 10%
  const isCollapsed = sidebarSize < 10;

  // Handle auth-based redirects
  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated) {
        navigate({ to: '/login', replace: true });
      } else if (!isAdmin) {
        navigate({ to: '/access-denied', replace: true });
      }
    }
  }, [isLoading, isAuthenticated, isAdmin, navigate, userData]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleLogout = async () => {
    setIsDropdownOpen(false);
    await logout();
    navigate({ to: '/login', replace: true });
  };

  const navItems = [
    { path: '/admin/user-management', label: 'User Management', icon: UserManagementIconSvg },
    { path: '/admin/quizzes', label: 'Modules & Quizzes', icon: QuizzesIconSvg },
    { path: '/admin/articles', label: 'Articles', icon: ArticlesIconSvg },
    { path: '/admin/campus-resources', label: 'Campus Resources', icon: CampusResourcesIconSvg },
    { path: '/admin/notifications', label: 'Notifications', icon: NotificationIconSvg },
    { path: '/admin/analytics', label: 'Analytics', icon: IoAnalyticsOutline },
  ];

  const isActive = (path: string) => {
    return matchRoute({ to: path }) !== false;
  };

  const currentPage = navItems.find(item => isActive(item.path));
  const pageName = currentPage?.label || 'Admin';
  const PageIcon = currentPage?.icon as React.ComponentType<React.SVGProps<SVGSVGElement>> | undefined;

  if (isLoading || !isAuthenticated || !isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand"></div>
      </div>
    );
  }

  return (
    <ResizablePanelGroup orientation="horizontal" className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <ResizablePanel
        defaultSize="20%"
        minSize="5%"
        maxSize="35%"
        onResize={(size) => {
          setSidebarSize(size.asPercentage);
        }}
        className="bg-white flex flex-col"
      >
        {/* Logo */}
        <div className={`p-2 border-b border-gray-200 ${isCollapsed ? 'flex justify-center' : ''}`}>
          {!isCollapsed ? (
            <img
              src={logoHorizontal}
              alt="Parenting First"
              className="h-10"
            />
          ) : (
            <div className="h-10 w-10 bg-brand rounded-lg flex items-center justify-center text-white font-bold text-xl">
              P
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className={`flex-1 py-6 ${isCollapsed ? 'px-2' : 'pl-3'}`}>
          {navItems.map((item) => {
            const Icon = item.icon as React.ComponentType<React.SVGProps<SVGSVGElement>>;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                title={isCollapsed ? item.label : undefined}
                className={`flex items-center mb-2 text-base font-medium transition-colors ${
                  isCollapsed
                    ? 'justify-center p-3 rounded-lg'
                    : 'gap-3 px-3 py-3 rounded-tl-lg rounded-bl-lg'
                } ${
                  active
                    ? 'bg-brand text-white'
                    : 'text-[#3D3D3D] hover:bg-gray-100'
                }`}
              >
                <span className={`w-6 h-6 flex items-center justify-center ${active ? '[&_path]:fill-white' : '[&_path]:fill-[#3D3D3D]'}`}>
                  <Icon />
                </span>
                {!isCollapsed && item.label}
              </Link>
            );
          })}
        </nav>
      </ResizablePanel>

      {/* Resize Handle */}
      <ResizableHandle withHandle className="w-1 bg-gray-300 hover:bg-brand hover:w-1.5 transition-all" />

      {/* Main Content Area */}
      <ResizablePanel defaultSize="80%" className="flex flex-col min-w-0">
        {/* Top Header */}

        <header className="bg-brand px-8 py-6 h-[209px]">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              {PageIcon && (
                <span className="w-9 h-9 flex items-center justify-center [&_path]:fill-white [&_svg]:w-full [&_svg]:h-full">
                  <PageIcon color={'#fff'}/>
                </span>
              )}
              <h1 className="text-2xl font-semibold text-white">
                {pageName}
              </h1>
            </div>
            {/* User Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center justify-between bg-white opacity-50 rounded-full px-1.5 py-1.5 hover:opacity-60 transition-opacity cursor-pointer"
              >
                <FaUserCircle className={'mr-2'} size={32} />
                <span className="text-sm font-medium text-text-primary mr-3">
                  {userData ? `${userData?.firstName} ${userData?.lastName ?? ''}`: 'Admin'}
                </span>
                <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    Log out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 bg-[#ffffff08] min-w-0">
          <div className={'px-6 min-w-0'}>
            <div className={'bg-white rounded-lg shadow-md p-5 pb-0 -mt-[120px] mb-6 h-[calc(100vh-110px)] overflow-auto min-w-0'}>
            <Outlet />
            </div>
          </div>

        </main>
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}
